import React, { createContext, useContext, useState, ReactNode } from 'react';

interface SidebarContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  /** True once the user has expanded the rail at least once (ever). Drives the one-time "More options" nudge. */
  hasEverExpanded: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  // Always begin on the bare immersive rail. The expanded/collapsed choice persists
  // for the session (React state) but every fresh app load starts collapsed again.
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Once the user has popped the rail open even once, we never nudge them again.
  // Guarded: embedded contexts (e.g. the widget iframe) can have storage access
  // blocked entirely, where `localStorage` throws SecurityError rather than
  // returning null.
  const [hasEverExpanded, setHasEverExpanded] = useState(() => {
    try {
      return localStorage.getItem('sidebarEverExpanded') === 'true';
    } catch {
      return false;
    }
  });

  const handleSetSidebarOpen = (open: boolean) => {
    setSidebarOpen(open);
    if (open && !hasEverExpanded) {
      setHasEverExpanded(true);
      try {
        localStorage.setItem('sidebarEverExpanded', 'true');
      } catch {
        // storage inaccessible (e.g. embedded iframe) — in-memory state still updates
      }
    }
  };

  return (
    <SidebarContext.Provider value={{ sidebarOpen, setSidebarOpen: handleSetSidebarOpen, hasEverExpanded }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
